import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Mail,
  Lock,
  User,
  Loader,
  TrendingUp,
  BarChart3,
  Target,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import axios from "../config/axios";
import toast from "react-hot-toast";

/**
 * Onboarding Flow Component
 * Manages the user journey from analysis start to signup/login
 */
const OnboardingFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signIn, devLogin, devRegister } = useAuth();

  // State from landing page
  const { sessionToken, analysisId } = location.state || {};

  // Component state
  const currentStep = "signup"; // Always show signup since we're not checking status
  const analysisStatus = "processing"; // Analysis is always processing in background
  const [isRegisterMode, setIsRegisterMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    displayName: "",
  });

  // Redirect if no session token
  useEffect(() => {
    if (!sessionToken) {
      // toast.error("No analysis session found. Starting over...");
      navigate("/analyze");
      return;
    }
  }, [sessionToken, navigate]);

  // Check if account already exists for this session
  useEffect(() => {
    const checkAccountStatus = async () => {
      if (!sessionToken) return;

      try {
        setCheckingAccount(true);
        const response = await axios.get(`/api/public/status/${sessionToken}`);

        if (response.data.hasAccount) {
          setHasExistingAccount(true);
          setIsRegisterMode(false); // Switch to login mode
          toast.info(
            "An account has already been created for this analysis. Please log in to view your results.",
          );
        }
      } catch (error) {
        console.error("Error checking account status:", error);
      } finally {
        setCheckingAccount(false);
      }
    };

    checkAccountStatus();
  }, [sessionToken]);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      // Small delay to ensure auth token is set in axios
      setTimeout(() => {
        linkSessionAndRedirect();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const linkSessionAndRedirect = async () => {
    try {
      // Only link if we have a sessionToken
      if (sessionToken) {
        console.log("Linking session:", sessionToken, "to user");
        // Link the session to the user
        // Authorization header is already set by AuthContext
        await axios.post("/api/subscription/link-session", {
          sessionToken: sessionToken,
        });
        console.log("Session linked successfully");
      } else {
        console.log("No session token to link");
      }

      // Redirect to CRO results or list page
      if (analysisId) {
        navigate(`/cro-analyzer/${analysisId}`);
      } else {
        navigate("/cro-analyzer");
      }
    } catch (error) {
      console.error("Error linking session:", error);
      // Continue anyway
      if (analysisId) {
        navigate(`/cro-analyzer/${analysisId}`);
      } else {
        navigate("/cro-analyzer");
      }
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If account exists, only allow login
      if (hasExistingAccount) {
        await devLogin(formData.email, formData.password);
        toast.success("Welcome back!");
      } else if (isRegisterMode) {
        // Only allow registration if no account exists
        await devRegister(
          formData.email,
          formData.password,
          formData.displayName,
        );
        toast.success("Account created successfully!");
      } else {
        // Regular login
        await devLogin(formData.email, formData.password);
        toast.success("Welcome back!");
      }

      // User state will update via AuthContext, triggering redirect
    } catch (error) {
      // Error handling is done in AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      await signIn();
      toast.success("Signed in successfully!");
      // User state will update via AuthContext, triggering redirect
    } catch (error) {
      // Error handling is done in AuthContext
    } finally {
      setLoading(false);
    }
  };

  const renderSignupStep = () => {
    // Show loading state while checking account status
    if (checkingAccount) {
      return (
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking session status...</p>
        </div>
      );
    }

    return (
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader className="w-10 h-10 text-blue-600 animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {hasExistingAccount ? "Welcome Back!" : "Create Your Account"}
          </h2>
          <p className="text-gray-600 mb-2">
            Your CRO analysis is running in the background
          </p>
          <p className="text-sm text-blue-600 font-medium">
            {hasExistingAccount
              ? "Log in to view your results when ready!"
              : "Sign up now to view your results when ready!"}
          </p>
        </div>

        {/* Auth Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Google Sign In */}
          <button
            onClick={handleGoogleAuth}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5 mr-2"
            />
            Continue with Google
          </button>

          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleEmailAuth} className="mt-6 space-y-4">
            {isRegisterMode && !hasExistingAccount && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) =>
                      setFormData({ ...formData, displayName: e.target.value })
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                    required={isRegisterMode && !hasExistingAccount}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader className="animate-spin w-5 h-5 mr-2" />
                  Processing...
                </span>
              ) : hasExistingAccount ? (
                "Sign In & View Results"
              ) : isRegisterMode ? (
                "Create Account & View Results"
              ) : (
                "Sign In & View Results"
              )}
            </button>
          </form>

          {/* Only show toggle between login/register if no account exists */}
          {!hasExistingAccount && (
            <p className="mt-4 text-center text-sm text-gray-600">
              {isRegisterMode
                ? "Already have an account?"
                : "Don't have an account?"}{" "}
              <button
                type="button"
                onClick={() => setIsRegisterMode(!isRegisterMode)}
                className="font-medium text-blue-600 hover:text-blue-700"
              >
                {isRegisterMode ? "Sign in" : "Sign up"}
              </button>
            </p>
          )}

          {/* Show message if account exists */}
          {hasExistingAccount && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 text-center">
                An account was already created for this analysis. Please log in
                with the credentials you used when creating the account.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-6">
      <div className="max-w-2xl w-full">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div
              className={`flex items-center ${analysisStatus ? "text-green-600" : "text-gray-400"}`}
            >
              <div
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                  analysisStatus
                    ? "border-green-600 bg-green-100"
                    : "border-gray-300"
                }`}
              >
                {analysisStatus === "completed" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <span className="text-sm">1</span>
                )}
              </div>
              <span className="ml-2 text-sm font-medium">Analysis</span>
            </div>

            <div className="w-16 h-0.5 bg-gray-300" />

            <div
              className={`flex items-center ${currentStep === "signup" ? "text-blue-600" : "text-gray-400"}`}
            >
              <div
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                  currentStep === "signup"
                    ? "border-blue-600 bg-blue-100"
                    : "border-gray-300"
                }`}
              >
                <span className="text-sm">2</span>
              </div>
              <span className="ml-2 text-sm font-medium">Account</span>
            </div>

            <div className="w-16 h-0.5 bg-gray-300" />

            <div className="flex items-center text-gray-400">
              <div className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center">
                <span className="text-sm">3</span>
              </div>
              <span className="ml-2 text-sm font-medium">Results</span>
            </div>
          </div>
        </div>

        {/* Dynamic Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderSignupStep()}
        </div>

        {/* Benefits reminder */}
        {currentStep === "signup" && (
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Conversion Insights</p>
            </div>
            <div className="text-center">
              <BarChart3 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Competitor Analysis</p>
            </div>
            <div className="text-center">
              <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Action Plan</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
